import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import AddCrl from "layouts/admin/crls/modals/AddCrl";
import EditCrl from "layouts/admin/crls/modals/EditCrl";
import ResetCrlPassword from "layouts/admin/crls/modals/ResetCrlPassword";
import DeleteCrl from "layouts/admin/crls/modals/DeleteCrl";
import CrlsDataTable from "layouts/admin/crls/data/CrlsDataTable";
import { getAllCrls, getAllCrlsIncludingDeleted } from "services/crlService";
import AppSnackbar from "layouts/notifications/Snackbar";
import { useTranslation } from "hooks/useTranslation";
import { useTableSelectionModals } from "hooks/useTableSelectionModals";

function ManageCrlsContent() {
  const { t } = useTranslation();
  const [openAddCrl, setOpenAddCrl] = useState(false);
  const {
    selectedItem: selectedCrl,
    openEdit,
    openReset,
    openDelete,
    handleEdit,
    handleReset,
    handleDelete,
    handleCloseDelete,
    handleCloseEdit,
    handleCloseReset,
    onPaginationChange,
  } = useTableSelectionModals();
  const [crlList, setCrlList] = useState([]);
  const [deletedCrlList, setDeletedCrlList] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, type: "", message: "" });

  const showSnackbar = (type, message) => {
    setSnackbar({ open: true, type, message });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false, type: "", message: "" }));
  };

  const fetchCrlsData = async () => {
    try {
      const data = await getAllCrls();
      setCrlList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching CRLs:", error);
      showSnackbar("error", t("crls.notifications.loadFailed", "Failed to load data collectors"));
    }
  };

  const fetchDeletedCrlsData = async () => {
    try {
      const all = await getAllCrlsIncludingDeleted();
      const deleted = (Array.isArray(all) ? all : []).filter((c) => c.is_deleted === 1);
      setDeletedCrlList(deleted);
    } catch (error) {
      console.error("Error fetching deleted CRLs:", error);
    }
  };

  const refreshAll = async () => {
    await fetchCrlsData();
    await fetchDeletedCrlsData();
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };

  const activeTable = CrlsDataTable({
    crls: crlList,
    onEdit: handleEdit,
    onReset: handleReset,
    onDelete: handleDelete,
    t,
    showActions: true,
  });

  const deletedTable = CrlsDataTable({
    crls: deletedCrlList,
    t,
    showActions: false,
  });

  const { columns, rows } = activeTab === 0 ? activeTable : deletedTable;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={1} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6">
                {t("crls.title", "Data Collector Management")}
              </MDTypography>
              {activeTab === 0 && (
                <MDButton variant="gradient" color="info" onClick={() => setOpenAddCrl(true)}>
                  <Icon>add</Icon>&nbsp;{t("crls.buttons.addCrl", "Add Collector")}
                </MDButton>
              )}
            </MDBox>

            <MDBox mt={3}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  boxShadow: 1,
                  "& .MuiTab-root": {
                    minHeight: "48px",
                    textTransform: "none",
                    fontSize: "1rem",
                    fontWeight: 500,
                    color: "#7b809a",
                    "&.Mui-selected": {
                      color: "#1976d2",
                      fontWeight: 600,
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1976d2",
                    height: "3px",
                  },
                }}
              >
                <Tab label={t("crls.tabs.activeCrls", "Active Collectors")} />
                <Tab label={t("crls.tabs.deletedCrls", "Deleted Collectors")} />
              </Tabs>
            </MDBox>

            <MDBox pt={3} pb={3}>
              <Card
                sx={{
                  boxShadow: 4,
                  borderRadius: 2,
                  overflow: "visible",
                  backgroundColor: "#f9fafe",
                  border: "1px solid #e0e0e0",
                }}
              >
                <MDBox>
                  <DataTable
                    key={activeTab}
                    table={{ columns, rows }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: 5, entries: [5, 10, 15, 20, 25] }}
                    showTotalEntries={true}
                    canSearch={true}
                    pagination={{ variant: "gradient", color: "info" }}
                    noEndBorder={false}
                    onPaginationChange={onPaginationChange}
                  />
                </MDBox>
              </Card>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <AddCrl
        open={openAddCrl}
        onClose={() => setOpenAddCrl(false)}
        onSuccess={refreshAll}
        title={t("crls.modals.addCrl", "Add Data Collector")}
      />
      {selectedCrl && (
        <EditCrl
          open={openEdit}
          onClose={handleCloseEdit}
          crl={selectedCrl}
          onSuccess={(message) => {
            refreshAll();
            if (message) showSnackbar("success", message);
          }}
          title={t("crls.modals.editCrl", "Edit Data Collector")}
        />
      )}
      <ResetCrlPassword
        open={openReset}
        onClose={handleCloseReset}
        crl={selectedCrl}
        onSuccess={refreshAll}
        title={t("crls.modals.resetPassword", "Reset Password")}
      />
      <DeleteCrl
        key={selectedCrl?.id ?? "closed"}
        open={openDelete}
        onClose={handleCloseDelete}
        crl={selectedCrl}
        onSuccess={(message) => {
          refreshAll();
          if (message) showSnackbar("success", message);
        }}
        title={t("crls.modals.deleteCrl", "Delete Data Collector")}
      />
      <AppSnackbar
        type={snackbar.type}
        message={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
      />
    </DashboardLayout>
  );
}

export default function ManageCrls() {
  return <ManageCrlsContent />;
}
